import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Icon, Text } from '@quintoandar-tokko/jopijs';
import TrendChip from '../TrendChip';
import styles from './dashboardKPICards.module.scss';
import useFetchDashboard from '../customHooks/useFetchDashboard';
import Skeletons from '@/components/Skeletons/Skeletons';
import ToolTip from '@/components/ToolTip/ToolTip';
import { useDndCardContext } from '../DndCard/context';
import { useTrackKPICards } from '../customHooks/useTrackDashboard';
import { KPI_CALLBACKS, KPI_TEXTS } from './kpiConfig';
import useOnClickOutside from '@/components/shared/Hooks';
import { setEdition } from '@/store/slices/components/dashboard/DashboardSlice';
import TooltipLink from '@/components/TooltipLink';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';

const scrollInto = (selector, offset = 16) => {
  const headerHeight = 56;
  const fixedFiltersHeights = 72;
  const totalOffset = headerHeight + fixedFiltersHeights + offset;

  const element = document.querySelector(selector);
  if (element) {
    const y =
      element.getBoundingClientRect().top + window.scrollY - totalOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }
};

export default function KPICard({ kpiId, trackKey, className = '', ...rest }) {
  const dispatch = useDispatch();
  const track = useTrackKPICards();
  const { edition, kpiCards } = useSelector((state) => state.DashboardSlice);
  const { ref, isStale, useFetchOnViewport } = useFetchDashboard();
  const { visible: dndVisible } = useDndCardContext();
  const kpiCard = kpiCards?.[kpiId];
  const cardRef = useRef(null);
  const [isCustomizeTooltipOpen, setIsCustomizeTooltipOpen] = useState(false);

  if (!kpiCard) return null;

  const { title = '', tooltip = '' } = KPI_TEXTS[kpiId] || {};
  const fetchCallback = KPI_CALLBACKS[kpiId];
  const {
    hasFetched,
    loading,
    value,
    variation,
    percentage_status: percentageStatus,
    icon,
    cardSelector,
  } = kpiCard;

  useOnClickOutside(cardRef, isCustomizeTooltipOpen, setIsCustomizeTooltipOpen);

  useFetchOnViewport(
    (query) => {
      if (fetchCallback) {
        fetchCallback(dispatch, query);
      }
    },
    [],
    { hasFetched }
  );

  useEffect(() => {
    setIsCustomizeTooltipOpen(false);
  }, [edition.active, loading]);

  const setCardRefs = (node) => {
    cardRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  const handleCustomize = () => {
    dispatch(setEdition({ key: 'active', value: true }));

    setTimeout(() => {
      scrollInto(cardSelector);
    }, 150);
  };

  return (
    <Box
      ref={setCardRefs}
      className={`${styles.kpiCard} ${className}`}
      onClick={() => {
        const element = document.querySelector(cardSelector);
        scrollInto(cardSelector);
        const trackFn = track?.[trackKey];
        if (trackFn) {
          trackFn();
        }
        if (!element) {
          setIsCustomizeTooltipOpen(true);
        }
      }}
      {...rest}
    >
      {loading || isStale ? (
        <>
          <Skeletons width="60%" height={20} />
          <Skeletons width="70%" height={32} />
        </>
      ) : (
        <>
          <Box className={styles.kpiCardHeader}>
            <Box className={styles.kpiCardHeaderLeft}>
              {edition.active && dndVisible && (
                <Icon
                  className={styles.hiddenIcon}
                  icon="icon-menu"
                  fontSize="12px"
                />
              )}
              <Text className={styles.kpiCardTitle}>{title}</Text>
            </Box>
            {!edition.active && (
              <ToolTip title={tooltip}>
                <Box className={styles.kpiInfoWrapper}>
                  <Icon icon="icon-info" className={styles.kpiInfo} />
                </Box>
              </ToolTip>
            )}
          </Box>

          <Box className={styles.kpiCardBody}>
            <Text className={styles.kpiCardValue}>{value}</Text>
            <TrendChip
              text={variation}
              percentageStatus={percentageStatus}
              icon={icon}
            />
          </Box>
          {isCustomizeTooltipOpen && (
            <Box ref={cardRef}>
              <TooltipLink
                title={Trans(
                  translations,
                  'CustomizeYourDashboardToSeeMoreInformationAboutThisReport'
                )}
                description={Trans(
                  translations,
                  'ChooseWhichCardsToHideOrShow'
                )}
                linkText={Trans(translations, 'KPITooltip.Customize')}
                linkOnClick={handleCustomize}
                handleClose={() => setIsCustomizeTooltipOpen(false)}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
